import React from 'react';
import PropTypes from 'prop-types';
import Typography from '../Typography.component';

// Renders a single cell within the table with appropriate styling based on its type (header, row, or footer)
const TableCell = ({ type, children }) => (
	<div className={`${type}-cell`}>
		{type === 'row' ? (
			<Typography.P>{children}</Typography.P>
		) : (
			<Typography.H4>{children}</Typography.H4>
		)}
	</div>
);

// Renders a complete table row by mapping column values to individual cells
const TableRow = ({ row }) => (
	<>
		{row.columns.map((colValue, colIndex) => (
			<TableCell type="row" key={colIndex}>
				{colValue}
			</TableCell>
		))}
	</>
);

/**
 * Grid-based table component that supports dynamic column layouts
 * with header, rows, and optional footer
 *
 * Sample data:
 * header: ['H1', 'H2', 'H3', 'H4']
 * rows: [
 *   { columns: ['R1C1', 'R1C2', 'R1C3', 'R1C4'] },
 *   { columns: ['R2C1', 'R2C2', 'R2C3', 'R2C4'] },
 * ]
 * footer: ['F1', 'F2', 'F3', 'F4']
 * */
const InlineTable = ({ header, rows, footer }) => {
	const columnCount = header.length;
	const tableClassName = `inline-table columns-${columnCount}`;

	return (
		<div className={tableClassName}>
			{header.map((headerItem, index) => (
				<TableCell type="header" key={index}>
					{headerItem}
				</TableCell>
			))}

			{rows.map((row, index) => (
				<TableRow key={index} row={row} />
			))}

			{footer?.map((footerItem, index) => (
				<TableCell type="footer" key={index}>
					{footerItem}
				</TableCell>
			))}
		</div>
	);
};

InlineTable.propTypes = {
	header: PropTypes.arrayOf(PropTypes.node).isRequired,
	rows: PropTypes.arrayOf(
		PropTypes.shape({
			columns: PropTypes.arrayOf(PropTypes.node).isRequired,
		})
	).isRequired,
	// Optional footer array
	footer: PropTypes.arrayOf(PropTypes.node),
};

InlineTable.defaultProps = {
	footer: null,
};

export default InlineTable;
